/**
 * Untamed v2.1 - Product Page JavaScript
 * ============================================
 * Handles variant selection, option availability, and media gallery interactions.
 */

(function() {
  'use strict';

  /**
   * Product Variant Selector Class
   * Handles variant selection, option availability updates, and UI synchronization
   */
  class ProductVariantSelector {
    constructor(config) {
      this.variants = config.variants || [];
      this.options = config.options || [];
      this.moneyFormat = config.moneyFormat || window.themeMoneyFormat || '{{amount}}';
      this.currentVariantId = config.currentVariantId;
      this.sectionId = config.sectionId || null;
      
      // Get container (scoped to section if sectionId provided)
      this.container = this.sectionId 
        ? document.querySelector(`[data-section-id="${this.sectionId}"]`)
        : document;
      
      if (!this.container) return;
      
      // Initialize selected options from current variant
      this.selectedOptions = {};
      const currentVariant = this.variants.find(v => v.id === this.currentVariantId);
      this.options.forEach((option, index) => {
        const optionKey = 'option' + (index + 1);
        this.selectedOptions[option.name] = currentVariant ? currentVariant[optionKey] : option.values[0];
      });
      
      this.init();
    }
    
    init() {
      this.syncUIState();
      this.updateOptionAvailability();
      this.bindEvents();
    }
    
    syncUIState() {
      // Sync button active states to match current variant
      Object.entries(this.selectedOptions).forEach(([optionName, optionValue]) => {
        const btn = this.container.querySelector(`[data-option-name="${optionName}"][data-option-value="${optionValue}"]`);
        if (btn) {
          const container = btn.closest('.product-variants-block__pills, .product-variants-block__swatches, .product-variants-block__rich-media');
          if (container) {
            container.querySelectorAll('button').forEach(b => b.classList.remove('active'));
          }
          btn.classList.add('active');
          
          const label = btn.closest('.product-variants-block__option')?.querySelector('.product-variants-block__selected-value');
          if (label) label.textContent = ' ' + optionValue;
        }
      });
    }
    
    bindEvents() {
      // Pills and swatches
      this.container.querySelectorAll('.product-variants-block__pill, .product-variants-block__swatch, .product-variants-block__rich-media-item').forEach(btn => {
        btn.addEventListener('click', (e) => this.handleOptionClick(e));
      });
      
      // Dropdowns
      this.container.querySelectorAll('.product-variants-block__dropdown').forEach(dropdown => {
        dropdown.addEventListener('select:change', (e) => this.handleDropdownChange(e, dropdown));
      });
    }
    
    handleOptionClick(e) {
      const btn = e.currentTarget;
      if (btn.disabled) return;
      
      const optionName = btn.dataset.optionName;
      const optionValue = btn.dataset.optionValue;
      
      // Update active state
      const container = btn.closest('.product-variants-block__pills, .product-variants-block__swatches, .product-variants-block__rich-media');
      if (container) {
        container.querySelectorAll('button').forEach(b => b.classList.remove('active'));
      }
      btn.classList.add('active');
      
      // Update label
      const label = btn.closest('.product-variants-block__option')?.querySelector('.product-variants-block__selected-value');
      if (label) label.textContent = ' ' + optionValue;
      
      // Update selection and find variant
      this.selectedOptions[optionName] = optionValue;
      this.updateOptionAvailability();
      
      const variant = this.findVariant();
      if (variant) {
        this.updateVariant(variant);
      }
    }
    
    handleDropdownChange(e, dropdown) {
      const optionName = dropdown.dataset.optionName;
      const optionValue = e.detail.value;
      
      const label = dropdown.closest('.product-variants-block__option')?.querySelector('.product-variants-block__selected-value');
      if (label) label.textContent = ' ' + optionValue;
      
      this.selectedOptions[optionName] = optionValue;
      this.updateOptionAvailability();
      
      const variant = this.findVariant();
      if (variant) {
        this.updateVariant(variant);
      }
    }
    
    findVariant() {
      return this.variants.find(variant => {
        return this.options.every((option, index) => {
          const optionKey = 'option' + (index + 1);
          return variant[optionKey] === this.selectedOptions[option.name];
        });
      });
    }
    
    updateOptionAvailability() {
      this.options.forEach((option, optionIndex) => {
        const optionKey = 'option' + (optionIndex + 1);
        
        option.values.forEach(value => {
          // Check if ANY variant with this option value is available
          // considering the OTHER currently selected options
          const isAvailable = this.variants.some(variant => {
            if (variant[optionKey] !== value) return false;
            if (!variant.available) return false;
            
            // Check if matches all OTHER selected options
            return this.options.every((otherOption, otherIndex) => {
              if (otherIndex === optionIndex) return true;
              const otherKey = 'option' + (otherIndex + 1);
              return variant[otherKey] === this.selectedOptions[otherOption.name];
            });
          });
          
          // Find and update button
          const btn = this.container.querySelector(
            `.product-variants-block__pill[data-option-name="${option.name}"][data-option-value="${value}"],
             .product-variants-block__swatch[data-option-name="${option.name}"][data-option-value="${value}"],
             .product-variants-block__rich-media-item[data-option-name="${option.name}"][data-option-value="${value}"]`
          );
          
          if (btn) {
            btn.disabled = !isAvailable;
          }
        });
      });
    }
    
    formatMoney(cents) {
      if (window.formatMoney) {
        return window.formatMoney(cents, this.moneyFormat);
      }
      if (window.formatMoneyFallback) {
        return window.formatMoneyFallback(cents);
      }
      return (Number(cents || 0) / 100).toFixed(2);
    }
    
    updateVariant(variant) {
      const isAvailable = variant.available === true;
      
      // Update hidden variant inputs
      this.container.querySelectorAll('input[name="id"], [data-variant-id]').forEach(input => {
        input.value = variant.id;
      });
      
      // Update price displays
      this.container.querySelectorAll('[id^="product-price-current"]').forEach(el => {
        el.textContent = this.formatMoney(variant.price);
        el.classList.toggle('product-price-block__current--on-sale', 
          variant.compare_at_price && variant.compare_at_price > variant.price);
      });
      
      this.container.querySelectorAll('[id^="product-price-compare"]').forEach(el => {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          el.textContent = this.formatMoney(variant.compare_at_price);
          el.style.display = '';
        } else {
          el.style.display = 'none';
        }
      });
      
      // Update add to cart buttons
      this.container.querySelectorAll('.product-add-to-cart-block__btn').forEach(btn => {
        const btnText = btn.querySelector('[data-add-to-cart-text]');
        btn.disabled = !isAvailable;
        if (btnText) btnText.textContent = isAvailable ? 'Add to Cart' : 'Sold Out';
      });
      
      // Update buy now buttons
      this.container.querySelectorAll('.shopify-payment-button button').forEach(btn => {
        btn.disabled = !isAvailable;
      });
      this.container.querySelectorAll('.product-add-to-cart-block__dynamic-checkout').forEach(container => {
        container.style.display = isAvailable ? '' : 'none';
      });
      
      // Update URL
      if (!this.sectionId) {
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
      
      // Update media gallery
      if (variant.featured_media) {
        document.dispatchEvent(new CustomEvent('product:media-change', {
          detail: { mediaId: variant.featured_media.id.toString(), sectionId: this.sectionId }
        }));
      }
      
      // Update stock block
      this.updateStockBlock(variant, isAvailable);
      
      // Dispatch event for other blocks
      window.dispatchEvent(new CustomEvent('variant:changed', {
        detail: { variant: variant }
      }));
    }
    
    updateStockBlock(variant, isAvailable) {
      this.container.querySelectorAll('.product-stock-block').forEach(stockBlock => {
        stockBlock.classList.remove('product-stock-block--in-stock', 'product-stock-block--low-stock', 'product-stock-block--out-of-stock');
        
        const message = stockBlock.querySelector('.product-stock-block__text');
        const barFill = stockBlock.querySelector('.product-stock-block__bar-fill');
        const threshold = parseInt(stockBlock.dataset.lowStockThreshold) || 5;
        const maxFillPercent = 80;
        let progressPercent = 0;
        
        if (!isAvailable) {
          stockBlock.classList.add('product-stock-block--out-of-stock');
          if (message) message.textContent = 'Out of Stock';
          progressPercent = 0;
        } else if (variant.inventory_management !== 'shopify') {
          stockBlock.classList.add('product-stock-block--in-stock');
          if (message) message.textContent = 'In Stock';
          progressPercent = maxFillPercent;
        } else if (variant.inventory_quantity <= 0) {
          stockBlock.classList.add('product-stock-block--in-stock');
          if (message) message.textContent = 'In Stock';
          progressPercent = maxFillPercent;
        } else if (variant.inventory_quantity <= threshold) {
          stockBlock.classList.add('product-stock-block--low-stock');
          if (message) message.textContent = `Only ${variant.inventory_quantity} left!`;
          progressPercent = Math.min((variant.inventory_quantity / threshold) * maxFillPercent, maxFillPercent);
        } else {
          stockBlock.classList.add('product-stock-block--in-stock');
          if (message) message.textContent = 'In Stock';
          progressPercent = maxFillPercent;
        }
        
        if (barFill) {
          barFill.style.setProperty('--progress', progressPercent + '%');
        }
      });
    }
  }

  /**
   * Product Media Gallery Class
   * Handles thumbnail navigation, swipe gestures, and media switching
   */
  class ProductMediaGallery {
    constructor(container) {
      this.container = container;
      this.id = container.id;
      this.layout = container.dataset.layout || 'collage';
      this.mobileLayout = container.dataset.mobileLayout || 'thumbnail';
      this.enableLightbox = container.dataset.enableLightbox === 'true';
      
      this.thumbnailsScrolls = [];
      this.desktopFeatured = container.querySelector('.product-media-gallery__featured');
      this.thumbnails = [];
      
      // Mobile carousel elements
      this.carouselSlider = container.querySelector('.product-media-gallery__carousel-slider');
      this.carouselItems = container.querySelectorAll('.product-media-gallery__carousel-item');
      
      this.currentIndex = 0;
      this.allMedia = [];
      
      this.refreshActiveThumbnails();
      // Build media data from thumbnails
      this.buildMediaData();
      
      this.init();
    }

    getActiveThumbnailScope() {
      const isDesktop = window.matchMedia('(min-width: 990px)').matches;

      if (isDesktop && (this.layout === 'thumbnail' || this.layout === 'thumbnail_left')) {
        return this.container.querySelector('.product-media-gallery__desktop-thumbnails');
      }

      if (this.mobileLayout === 'thumbnail') {
        return this.container.querySelector('.product-media-gallery__mobile-thumbnails');
      }

      return null;
    }

    refreshActiveThumbnails() {
      const scope = this.getActiveThumbnailScope();
      this.thumbnailsScrolls = scope ? scope.querySelectorAll('.product-media-gallery__thumbnails-scroll') : [];
      this.thumbnails = scope ? scope.querySelectorAll('.product-media-gallery__thumbnail') : [];
    }

    // Centers the active thumbnail inside its own scroll rail only — never the page.
    scrollThumbnailIntoRail(thumb) {
      if (!thumb) return;
      const rail = thumb.closest('.product-media-gallery__thumbnails-scroll');
      if (!rail) return;

      const isVertical = this.layout === 'thumbnail_left' && window.matchMedia('(min-width: 990px)').matches;
      if (isVertical) {
        const target = thumb.offsetTop - (rail.clientHeight - thumb.clientHeight) / 2;
        rail.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
      } else {
        const target = thumb.offsetLeft - (rail.clientWidth - thumb.clientWidth) / 2;
        rail.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
      }
    }

    getSlideCount() {
      this.refreshActiveThumbnails();
      if (this.thumbnails.length && this.allMedia.length !== this.thumbnails.length) {
        this.buildMediaData();
      }

      return Math.max(this.thumbnails.length, this.carouselItems.length, this.allMedia.length);
    }
    
    buildMediaData() {
      this.allMedia = [];

      // Extract media info from each thumbnail
      this.thumbnails.forEach((thumb, fallbackIndex) => {
        const img = thumb.querySelector('.product-media-gallery__thumbnail-image');
        if (img) {
          const parsedIndex = parseInt(thumb.dataset.index, 10);
          const index = Number.isNaN(parsedIndex) ? fallbackIndex : parsedIndex;

          // Get high-res src from thumbnail src by replacing width
          const thumbSrc = img.src;
          const highResSrc = thumbSrc.replace(/width=\d+/, 'width=1200');
          const lightboxSrc = thumbSrc.replace(/width=\d+/, 'width=2400');
          
          this.allMedia[index] = {
            index: index,
            id: thumb.dataset.mediaId,
            type: 'image', // Could be extended for video/model
            src: highResSrc,
            srcset: `${thumbSrc.replace(/width=\d+/, 'width=600')} 600w, ${thumbSrc.replace(/width=\d+/, 'width=900')} 900w, ${highResSrc} 1200w`,
            lightboxSrc: lightboxSrc,
            alt: img.alt || ''
          };
        }
      });

      this.preloadFeaturedImages();
    }

    // Preload the featured-size (1200px) images so clicking a thumbnail swaps the
    // main image instantly instead of fetching on click. Deferred so it doesn't
    // compete with the initial render / first featured image.
    preloadFeaturedImages() {
      const run = () => {
        this._featuredPreload = this.allMedia
          .filter(Boolean)
          .map((m) => {
            const im = new Image();
            im.decoding = 'async';
            im.src = m.src;
            return im;
          });
      };
      if ('requestIdleCallback' in window) {
        requestIdleCallback(run, { timeout: 1000 });
      } else {
        setTimeout(run, 200);
      }
    }
    
    init() {
      this.bindThumbnailClicks();
      this.bindMobileScrollSync();
      this.bindVariantMediaChange();

      if (this.enableLightbox) {
        this.bindLightbox();
      }
    }
    
    bindThumbnailClicks() {
      this.container.addEventListener('click', (e) => {
        const thumb = e.target.closest('.product-media-gallery__thumbnail');
        if (!thumb || !this.container.contains(thumb)) return;

        const activeScope = this.getActiveThumbnailScope();
        if (activeScope && !activeScope.contains(thumb)) return;

        const index = parseInt(thumb.dataset.index, 10);
        if (Number.isNaN(index)) return;
        this.goToSlide(index);
      });
    }
    
    bindMobileScrollSync() {
      if (!this.carouselSlider || !this.carouselItems.length) return;

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
              const idx = parseInt(entry.target.dataset.index, 10);
              if (!Number.isNaN(idx)) this.syncFromScroll(idx);
            }
          });
        }, { root: this.carouselSlider, threshold: 0.6 });

        this.carouselItems.forEach((item) => io.observe(item));
        this.mobileScrollObserver = io;
      } else {
        let scrollTimer;
        this.carouselSlider.addEventListener('scroll', () => {
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(() => {
            const width = this.carouselSlider.clientWidth || 1;
            this.syncFromScroll(Math.round(this.carouselSlider.scrollLeft / width));
          }, 80);
        }, { passive: true });
      }
    }

    syncFromScroll(index) {
      this.currentIndex = index;

      this.refreshActiveThumbnails();
      this.thumbnails.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
      });

      this.scrollThumbnailIntoRail(this.thumbnails[index]);
    }

    bindVariantMediaChange() {
      document.addEventListener('product:media-change', (e) => {
        const mediaId = e.detail.mediaId;
        const activeScope = this.getActiveThumbnailScope();
        let thumb = activeScope ? activeScope.querySelector(`[data-media-id="${mediaId}"]`) : null;
        if (!thumb) {
          thumb = this.container.querySelector(`[data-media-id="${mediaId}"]`);
        }
        
        if (thumb && thumb.dataset.index) {
          this.goToSlide(parseInt(thumb.dataset.index, 10));
        }
      });
    }
    
    bindLightbox() {
      this.container.querySelectorAll('.product-media-gallery__image').forEach(img => {
        img.style.cursor = 'zoom-in';
        img.addEventListener('click', () => {
          const lightboxSrc = img.dataset.lightboxSrc || img.src;
          this.openLightbox(lightboxSrc, img.alt);
        });
      });
    }
    
    goToSlide(index) {
      const maxIndex = this.getSlideCount();
      if (index < 0 || index >= maxIndex) return;
      
      this.currentIndex = index;
      
      // Update active thumbnail
      this.thumbnails.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
      });
      
      // Scroll active thumbnail into view within its rail (never the page)
      this.scrollThumbnailIntoRail(this.thumbnails[index]);

      // Update mobile carousel
      this.updateMobileCarousel(index);
      
      // Update desktop featured image
      this.updateDesktopFeaturedImage(index);
    }
    
    updateMobileCarousel(index) {
      if (!this.carouselSlider || !this.carouselItems.length) return;
      if (!this.carouselItems[index]) return;

      const left = index * this.carouselSlider.clientWidth;
      if (Math.abs(this.carouselSlider.scrollLeft - left) < 2) return;

      this.carouselSlider.scrollTo({ left: left, behavior: 'smooth' });
    }
    
    updateDesktopFeaturedImage(index) {
      if (!this.desktopFeatured || !this.allMedia[index]) return;
      
      const media = this.allMedia[index];
      const image = this.desktopFeatured.querySelector('.product-media-gallery__featured-image');
      
      if (image && media.type === 'image') {
        // Animate transition with GSAP if available
        if (typeof gsap !== 'undefined') {
          gsap.to(image, {
            opacity: 0,
            scale: 0.98,
            duration: 0.15,
            ease: 'power2.in',
            onComplete: () => {
              // Update image source
              image.src = media.src;
              image.srcset = media.srcset;
              image.alt = media.alt;
              image.dataset.lightboxSrc = media.lightboxSrc;
              
              // Update container data attributes
              this.desktopFeatured.dataset.mediaId = media.id;
              this.desktopFeatured.dataset.index = index;
              
              // Animate in
              gsap.to(image, {
                opacity: 1,
                scale: 1,
                duration: 0.2,
                ease: 'power2.out'
              });
            }
          });
        } else {
          // Fallback without animation
          image.src = media.src;
          image.srcset = media.srcset;
          image.alt = media.alt;
          image.dataset.lightboxSrc = media.lightboxSrc;
          this.desktopFeatured.dataset.mediaId = media.id;
          this.desktopFeatured.dataset.index = index;
        }
      }
    }

    openLightbox(src, alt) {
      // Create lightbox overlay
      const overlay = document.createElement('div');
      overlay.className = 'product-lightbox';
      overlay.innerHTML = `
        <div class="product-lightbox__backdrop"></div>
        <div class="product-lightbox__content">
          <img src="${src}" alt="${alt || ''}" class="product-lightbox__image">
          <button type="button" class="product-lightbox__close" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>
      `;
      
      document.body.appendChild(overlay);
      document.body.style.overflow = 'hidden';
      
      // Animate in with GSAP if available
      if (typeof gsap !== 'undefined') {
        gsap.fromTo(overlay, { opacity: 0 }, { opacity: 1, duration: 0.3 });
        gsap.fromTo(overlay.querySelector('.product-lightbox__image'), 
          { scale: 0.9, opacity: 0 }, 
          { scale: 1, opacity: 1, duration: 0.3, delay: 0.1 }
        );
      }
      
      // Close handlers
      const close = () => {
        if (typeof gsap !== 'undefined') {
          gsap.to(overlay, { 
            opacity: 0, 
            duration: 0.2, 
            onComplete: () => {
              overlay.remove();
              document.body.style.overflow = '';
            }
          });
        } else {
          overlay.remove();
          document.body.style.overflow = '';
        }
      };
      
      overlay.querySelector('.product-lightbox__close').addEventListener('click', close);
      overlay.querySelector('.product-lightbox__backdrop').addEventListener('click', close);
      overlay.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') close();
      });
    }
  }

  /**
   * Initialize galleries on DOM ready
   */
  function init() {
    document.querySelectorAll('.product-media-gallery').forEach(gallery => {
      new ProductMediaGallery(gallery);
    });
  }
  
  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  // Re-initialize on Shopify section refresh
  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const gallery = event.target.querySelector('.product-media-gallery');
      if (gallery) {
        new ProductMediaGallery(gallery);
      }
    });
  }
  
  // Expose for external use
  window.ProductMediaGallery = ProductMediaGallery;
  window.ProductVariantSelector = ProductVariantSelector;
})();

/**
 * Add lightbox styles dynamically
 */
(function() {
  const style = document.createElement('style');
  style.textContent = `
    .product-lightbox {
      position: fixed;
      inset: 0;
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .product-lightbox__backdrop {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.9);
    }
    
    .product-lightbox__content {
      position: relative;
      max-width: 90vw;
      max-height: 90vh;
    }
    
    .product-lightbox__image {
      max-width: 100%;
      max-height: 90vh;
      object-fit: contain;
    }
    
    .product-lightbox__close {
      position: absolute;
      top: -40px;
      right: 0;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      border: none;
      color: white;
      cursor: pointer;
      transition: opacity 0.2s;
    }
    
    .product-lightbox__close:hover {
      opacity: 0.7;
    }
  `;
  document.head.appendChild(style);

})();
